import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Compass, Plus, AlertOctagon, ChevronDown, ScaleIcon } from "lucide-react";

import { useCandidates } from "@/application/use-cases/candidates/use-candidates.queries";
import { useAllCandidateChoices } from "@/application/use-cases/candidates/use-candidate-choices.queries";
import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import { useDefaultWeightsPreset } from "@/application/use-cases/products/use-weights.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import {
  computeCandidateAggregate,
  type CoverageBreakdown,
} from "@/application/use-cases/candidates/compute-candidate-aggregates";
import { useComponentCriticality } from "@/application/use-cases/candidates/use-component-criticality.queries";
import { ActivePresetSelector } from "@/presentation/components/products/active-preset-selector";
import { CreateCandidateDialog } from "@/presentation/components/candidates/create-candidate-dialog";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { architectureComponents } from "@/presentation/data/architecture-components";
import type { CandidateRow } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/candidates/")({
  component: CandidatesPage,
});

const PROVENANCE_COLOR = {
  yangoReuse: "oklch(0.55 0.16 145)",
  offTheShelf: "oklch(0.55 0.17 190)",
  buildOnTop: "oklch(0.55 0.16 260)",
  moveoBuild: "oklch(0.62 0.16 50)",
  tbd: "oklch(0.85 0.005 240)",
} as const;

const COVERAGE_COLOR = {
  native: "oklch(0.55 0.17 145)",
  plugin: "oklch(0.55 0.17 260)",
  custom: "oklch(0.60 0.17 50)",
  track: "oklch(0.94 0.005 240)",
} as const;

function CandidatesPage() {
  const candidatesQ = useCandidates();
  const choicesQ = useAllCandidateChoices();
  const productsQ = useProducts();
  const scoresQ = useAllProductScores();
  const criteriaQ = useEvaluationCriteria();
  const presetQ = useDefaultWeightsPreset();
  const featuresQ = useFeaturesList();
  const fpQ = useAllFeatureProducts();
  const verdictsQ = useAllVerdicts();
  const criticalityQ = useComponentCriticality();
  const [createOpen, setCreateOpen] = useState(false);

  const candidates = candidatesQ.data ?? [];
  const allChoices = choicesQ.data ?? [];
  const products = productsQ.data ?? [];
  const productScores = scoresQ.data ?? [];
  const criteria = criteriaQ.data ?? [];
  const weights = presetQ.data?.weights ?? {};
  const features = featuresQ.data ?? [];
  const featureProducts = fpQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];
  const criticalityOverrides = criticalityQ.data ?? {};

  // Every query feeds the composite — gate the whole page until all resolve, so
  // we never flash composites computed from half-loaded (empty) inputs.
  const isLoading =
    candidatesQ.isLoading ||
    choicesQ.isLoading ||
    productsQ.isLoading ||
    scoresQ.isLoading ||
    criteriaQ.isLoading ||
    presetQ.isLoading ||
    featuresQ.isLoading ||
    fpQ.isLoading ||
    verdictsQ.isLoading ||
    criticalityQ.isLoading;

  const aggregates = useMemo(() => {
    return candidates.map((c) => {
      const choices = allChoices.filter((ch) => ch.candidate_id === c.id);
      const agg = computeCandidateAggregate({
        choices,
        products,
        productScores,
        criteria,
        features,
        featureProducts,
        verdicts,
        weights,
        criticalityOverrides,
      });
      return { candidate: c, agg };
    });
  }, [
    candidates,
    allChoices,
    products,
    productScores,
    criteria,
    features,
    featureProducts,
    verdicts,
    weights,
    criticalityOverrides,
  ]);

  const ranked = useMemo(() => {
    const aboveLine = aggregates.filter((a) => a.agg.dealbreakerOrigins.length === 0);
    const belowLine = aggregates.filter((a) => a.agg.dealbreakerOrigins.length > 0);
    aboveLine.sort((a, b) => (b.agg.composite ?? 0) - (a.agg.composite ?? 0));
    return { aboveLine, belowLine };
  }, [aggregates]);

  const topScore = ranked.aboveLine[0]?.agg.composite ?? null;

  if (isLoading) {
    return <CandidatesLoadingState />;
  }

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="candidates" />
      </div>
      <RouteHero
        eyebrow="Strategic options"
        icon={Compass}
        numeral={String(ranked.aboveLine.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix={`/ ${candidates.length} ranked`}
        title="Architecture candidates."
        summary="Each candidate is a composition of product choices per architecture component. Composite score aggregates from constituent products weighted by component criticality."
        meta={[
          { label: "Above line", value: ranked.aboveLine.length, tone: "cyan" },
          {
            label: "Below line",
            value: ranked.belowLine.length,
            tone: ranked.belowLine.length > 0 ? "red" : "default",
          },
          { label: "Components", value: architectureComponents.length },
          {
            label: "Top score",
            value: topScore !== null ? topScore.toFixed(2) : "—",
            tone: "green",
          },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Link
              to="/candidates/compare-summary"
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(1_0_0_/_0.18)] bg-[oklch(1_0_0_/_0.04)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.97_0.005_240)] transition-colors hover:bg-[oklch(1_0_0_/_0.08)]"
            >
              <ScaleIcon className="size-3" />
              Summary compare
            </Link>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex w-fit items-center gap-1.5 rounded-md border border-[oklch(0.72_0.17_190_/_0.40)] bg-[oklch(0.72_0.17_190_/_0.20)] px-3 py-1.5 text-[11.5px] font-medium text-[oklch(0.92_0.07_190)] transition-colors hover:bg-[oklch(0.72_0.17_190_/_0.30)]"
            >
              <Plus className="size-3" />
              New candidate
            </button>
          </div>
        }
      />

      <CreateCandidateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <ActivePresetSelector />
          <span className="text-muted-foreground text-[10.5px]">
            Switch the lens — composites re-rank live.
          </span>
        </div>
        <div className="text-muted-foreground flex items-center justify-between text-[10.5px] font-medium tracking-[0.12em] uppercase">
          <span>Ranked</span>
          <span>Composite / 5.00</span>
        </div>

        {candidates.length === 0 ? (
          <div className="glow-card p-8 text-center">
            <p className="text-foreground/70 text-sm">No candidates yet.</p>
            <p className="text-muted-foreground mt-1 text-[12px]">
              Create one to start composing product choices per architecture component.
            </p>
          </div>
        ) : (
          <>
            {ranked.aboveLine.map(({ candidate, agg }, idx) => (
              <CandidateCard key={candidate.id} candidate={candidate} agg={agg} rank={idx + 1} />
            ))}
            {ranked.belowLine.length > 0 ? (
              <>
                <div
                  className="mt-4 flex items-center gap-2 text-[10.5px] font-medium tracking-[0.12em] uppercase"
                  style={{ color: "oklch(0.55 0.20 25)" }}
                >
                  <AlertOctagon className="size-3" />
                  <span>Below the line — dealbreaker fails</span>
                </div>
                {ranked.belowLine.map(({ candidate, agg }) => (
                  <BelowLineCard key={candidate.id} candidate={candidate} agg={agg} />
                ))}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function CandidatesLoadingState() {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="bg-muted/40 h-44 animate-pulse rounded-2xl" />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="bg-muted/20 h-3 w-16 animate-pulse rounded" />
          <div className="bg-muted/20 h-3 w-28 animate-pulse rounded" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <CandidateCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/** Mirrors CandidateCard's layout: rank · name · composite, provenance bar, 4 stats. */
function CandidateCardSkeleton() {
  return (
    <div className="glow-card flex flex-col gap-3 p-5">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <div className="bg-muted/40 h-6 w-7 animate-pulse rounded" />
          <div className="flex flex-col gap-1.5">
            <div className="bg-muted/40 h-4 w-44 animate-pulse rounded" />
            <div className="bg-muted/20 h-3 w-28 animate-pulse rounded" />
          </div>
        </div>
        <div className="bg-muted/40 h-6 w-12 animate-pulse rounded" />
      </div>

      <div className="bg-muted/30 h-2.5 w-full animate-pulse rounded-full" />

      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <div className="bg-muted/20 h-2.5 w-14 animate-pulse rounded" />
            <div className="bg-muted/40 h-4 w-8 animate-pulse rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CandidateCard({
  candidate,
  agg,
  rank,
}: {
  candidate: CandidateRow;
  agg: ReturnType<typeof computeCandidateAggregate>;
  rank: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const total =
    agg.provenance.yangoReuse +
      agg.provenance.offTheShelf +
      agg.provenance.buildOnTop +
      agg.provenance.moveoBuild +
      agg.provenance.tbd || 1;

  return (
    <Link
      to="/candidates/$slug"
      params={{ slug: candidate.slug }}
      className="glow-card flex flex-col gap-3 p-5 transition-colors hover:border-[oklch(0.72_0.17_190_/_0.40)] print:break-inside-avoid"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <span className="font-data text-foreground/40 text-[22px] tabular-nums">
            {String(rank).padStart(2, "0")}
          </span>
          <div className="flex flex-col gap-0.5">
            <h3 className="text-[15px] leading-tight font-semibold">{candidate.name}</h3>
            <span className="text-foreground/60 text-[11.5px]">
              {agg.configuredComponentCount} component
              {agg.configuredComponentCount === 1 ? "" : "s"}
              {agg.tbdCount > 0 ? ` · ${agg.tbdCount} TBD` : ""}
            </span>
          </div>
        </div>
        <span
          className="font-data text-[22px] font-medium tabular-nums"
          style={{ color: "oklch(0.55 0.17 190)" }}
        >
          {agg.composite !== null ? agg.composite.toFixed(2) : "—"}
        </span>
      </div>

      <div
        className="relative flex h-2.5 overflow-hidden rounded-full"
        style={{
          background: "oklch(0.94 0.005 240)",
          boxShadow: "inset 0 0 0 1px oklch(0.91 0.008 240)",
        }}
      >
        <div
          style={{
            width: `${(agg.provenance.yangoReuse / total) * 100}%`,
            background: PROVENANCE_COLOR.yangoReuse,
          }}
        />
        <div
          style={{
            width: `${(agg.provenance.offTheShelf / total) * 100}%`,
            background: PROVENANCE_COLOR.offTheShelf,
          }}
        />
        <div
          style={{
            width: `${(agg.provenance.buildOnTop / total) * 100}%`,
            background: PROVENANCE_COLOR.buildOnTop,
          }}
        />
        <div
          style={{
            width: `${(agg.provenance.moveoBuild / total) * 100}%`,
            background: PROVENANCE_COLOR.moveoBuild,
          }}
        />
        <div
          style={{
            width: `${(agg.provenance.tbd / total) * 100}%`,
            background:
              "repeating-linear-gradient(45deg, transparent 0 4px, oklch(0.85 0.01 240) 4px 5px)",
          }}
        />
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Yango" value={agg.provenance.yangoReuse} color={PROVENANCE_COLOR.yangoReuse} />
        <Stat
          label="Off-the-shelf"
          value={agg.provenance.offTheShelf}
          color={PROVENANCE_COLOR.offTheShelf}
        />
        <Stat
          label="Build on top"
          value={agg.provenance.buildOnTop}
          color={PROVENANCE_COLOR.buildOnTop}
        />
        <Stat
          label="From Scratch / TBD"
          value={`${agg.provenance.moveoBuild} / ${agg.provenance.tbd}`}
          color={PROVENANCE_COLOR.moveoBuild}
        />
      </div>

      <div className="border-foreground/8 -mx-1 mt-1 flex flex-col gap-2 border-t pt-2">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="text-foreground/55 hover:text-foreground inline-flex items-center gap-1 self-start px-1 text-[10.5px] font-medium tracking-wider uppercase transition-colors print:hidden"
        >
          <ChevronDown
            className="size-3 transition-transform"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          />
          {expanded ? "Hide details" : "Show details"}
        </button>
        {expanded ? (
          <div className="grid grid-cols-3 gap-3 px-1 text-[11.5px]">
            <KV
              label="Time to MVP"
              value={
                candidate.time_to_mvp_weeks !== null ? `${candidate.time_to_mvp_weeks} wk` : "—"
              }
            />
            <KV
              label="TCO @ 24mo"
              value={formatTcoRange(candidate.tco_low_usd, candidate.tco_high_usd)}
            />
            <CoverageTile label="B2C coverage" value={agg.coverage.b2c} />
          </div>
        ) : null}
      </div>
    </Link>
  );
}

function CoverageTile({ label, value }: { label: string; value: CoverageBreakdown | null }) {
  return (
    <div className="flex flex-col gap-1 rounded-md bg-[oklch(0.97_0.005_240)] px-3 py-2">
      <span className="font-data text-foreground/55 text-[9.5px] tracking-[0.12em] uppercase">
        {label}
      </span>
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-data text-[12.5px] font-medium tabular-nums"
          style={{ color: value !== null ? COVERAGE_COLOR.native : undefined }}
          title="Share of in-scope features covered NATIVELY (out-of-the-box)."
        >
          {value !== null ? `${Math.round(value.nativeRatio * 100)}% native` : "—"}
        </span>
        {value !== null ? (
          <span className="font-data text-foreground/55 text-[10px] tabular-nums">
            / {Math.round(value.totalRatio * 100)}% total
          </span>
        ) : null}
      </div>
      <div
        className="relative flex h-1 overflow-hidden rounded-full"
        style={{ background: COVERAGE_COLOR.track }}
      >
        {value !== null ? (
          <>
            <div
              style={{
                width: `${value.nativeRatio * 100}%`,
                background: COVERAGE_COLOR.native,
              }}
            />
            <div
              style={{
                width: `${value.pluginRatio * 100}%`,
                background: COVERAGE_COLOR.plugin,
              }}
            />
            <div
              style={{
                width: `${value.customRatio * 100}%`,
                background: COVERAGE_COLOR.custom,
              }}
            />
          </>
        ) : null}
      </div>
      {value !== null ? (
        <span className="font-data text-foreground/55 text-[9.5px] tabular-nums">
          {value.nativeCount}N · {value.pluginCount}P · {value.customCount}C
        </span>
      ) : null}
    </div>
  );
}

function BelowLineCard({
  candidate,
  agg,
}: {
  candidate: CandidateRow;
  agg: ReturnType<typeof computeCandidateAggregate>;
}) {
  return (
    <Link
      to="/candidates/$slug"
      params={{ slug: candidate.slug }}
      className="glow-card flex flex-col gap-2 p-4 print:break-inside-avoid"
      style={{ borderLeft: "3px solid oklch(0.62 0.18 25)" }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-[14px] leading-tight font-semibold line-through decoration-[oklch(0.62_0.18_25)] decoration-2">
          {candidate.name}
        </h3>
        <span className="font-data text-foreground/40 text-[14px] tabular-nums">
          {agg.composite !== null ? agg.composite.toFixed(2) : "—"}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5 text-[11.5px]" style={{ color: "oklch(0.45 0.18 25)" }}>
        {agg.dealbreakerOrigins.slice(0, 4).map((o, i) => (
          <li key={`${o.productId}-${o.criterionId}-${i}`}>
            · {o.productName} fails {o.criterionName}
          </li>
        ))}
        {agg.dealbreakerOrigins.length > 4 ? (
          <li>· +{agg.dealbreakerOrigins.length - 4} more</li>
        ) : null}
      </ul>
    </Link>
  );
}

function Stat({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-foreground/60 flex items-center gap-1.5 text-[10.5px]">
        <span className="size-1.5 rounded-full" style={{ background: color }} />
        {label}
      </div>
      <span className="font-data text-[13px] font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function formatTcoRange(low: number | null, high: number | null): string {
  const k = (n: number) => `₪${Math.round(n / 1000)}k`;
  if (low !== null && high !== null) return `${k(low)}–${k(high).replace("₪", "")}`;
  if (low !== null) return `from ${k(low)}`;
  if (high !== null) return `up to ${k(high)}`;
  return "—";
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md bg-[oklch(0.97_0.005_240)] px-3 py-2">
      <span className="font-data text-foreground/55 text-[9.5px] tracking-[0.12em] uppercase">
        {label}
      </span>
      <span className="font-data text-[12.5px] font-medium tabular-nums">{value}</span>
    </div>
  );
}
